<template>
    <div :class="['EvieChatMessageUserBubble', { 'EvieChatMessageUserBubble--settled': isSettled }]">
        <div class="EvieChatMessageUserBubble__group">
            <p class="EvieChatMessageUserBubble__text">
                {{ message.content.text }}
            </p>
            <div :class="['EvieChatMessageUserBubble__footer', { 'EvieChatMessageUserBubble__footer--visible': isLast && isSettled }]">
                <span class="EvieChatMessageUserBubble__time">{{ formattedTime }}</span>
                <NewButton
                    class="EvieChatMessageUserBubble__copy"
                    variant="flat"
                    color="neutral"
                    size="icon-sm"
                    type="button"
                    aria-label="Copy message"
                    @click="copyText"
                >
                    <EidIcon :icon-src="copyIcon" />
                </NewButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.EvieChatMessageUserBubble {
    display: flex;
    justify-content: flex-end;
    width: 100%;

    &__group {
        display: flex;
        flex-direction: column;
        max-width: 70%;
    }

    &__text {
        padding: 10px 14px;
        border-radius: 18px 18px 4px 18px;
        background: var(--eidTechGray-10);
        color: var(--eidQualityBlack);
        font-size: 14px;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
        margin: 0;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 2px;
        margin-top: 4px;
        opacity: 0;
        transition: opacity 0.15s;

        &--visible,
        .EvieChatMessageUserBubble--settled:hover & {
            opacity: 1;
        }
    }

    &__time {
        font-size: 11px;
        color: var(--eidMediumGray);
        padding-right: 2px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
    import { faCheck } from '@fortawesome/pro-light-svg-icons';

    import { faCopy } from '@evidentid/dashboard-commons/assets/custom-icon-definitions';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { EvieThreadMessage } from '@/modules/evie-ai/models/EvieThreadMessage.model';

    export default defineComponent({
        name: 'EvieChatMessageUserBubble',
        components: { NewButton, EidIcon },
        props: {
            message: { type: Object as PropType<EvieThreadMessage>, required: true },
            isLast: { type: Boolean, default: false },
            isSettled: { type: Boolean, default: false },
        },
        data() {
            return {
                copyIcon: faCopy as IconDefinition,
                copiedTimeout: null as ReturnType<typeof setTimeout> | null,
            };
        },
        computed: {
            formattedTime(): string {
                return new Date(this.message.createdAt).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                });
            },
        },
        beforeUnmount() {
            if (this.copiedTimeout) {
                clearTimeout(this.copiedTimeout);
            }
        },
        methods: {
            copyText() {
                navigator.clipboard.writeText(this.message.content.text);
                this.copyIcon = faCheck as IconDefinition;
                if (this.copiedTimeout) {
                    clearTimeout(this.copiedTimeout);
                }
                this.copiedTimeout = setTimeout(() => {
                    this.copyIcon = faCopy as IconDefinition;
                }, 1000);
            },
        },
    });
</script>
